import { postContactsDirectoryOp, type UserDirectoryRow, type ExternalContactRow } from "@/lib/contactsDirectoryClient";

type Toast = (msg: string, variant?: "ok" | "err") => void;

type Params = {
  organizationId: string;
  toast: Toast;
  formatUnknownError: (e: unknown) => string;
  extForm: any;
  setExtForm: (v: any) => void;
  editingUserRow: UserDirectoryRow | null;
  setEditingUserRow: (v: UserDirectoryRow | null) => void;
  userManagerProfileIdSet: Set<string>;
  setUserFormBusy: (v: boolean) => void;
  isLikelyValidEmail: (v: string) => boolean;
  hasAtLeastTwoWords: (v: string) => boolean;
  load: () => Promise<void>;
  createEmptyExternalForm: () => any;
  parseTopicsText: (v: string) => string[];
  setRoleGuidance: (v: string) => void;
};

export function useAdminContactsSaveFlow(p: Params) {
  async function saveWithFallbackAcknowledge(body: Record<string, unknown>, confirmMessage: string, successMessage: string) {
    try {
      await postContactsDirectoryOp(p.organizationId, body);
      p.toast(successMessage, "ok");
      return true;
    } catch (e: unknown) {
      const msg = p.formatUnknownError(e);
      if (/general support fallback/i.test(msg)) {
        if (!window.confirm(confirmMessage)) return false;
        await postContactsDirectoryOp(p.organizationId, { ...body, allow_fallback_degrade: true });
        p.toast(successMessage, "ok");
        return true;
      }
      throw e;
    }
  }

  function setTopicEnabled(topic: string, enabled: boolean) {
    const set = new Set(p.parseTopicsText(p.extForm.topics));
    if (enabled) set.add(topic); else set.delete(topic);
    p.setExtForm((f: any) => ({ ...f, topics: [...set].join(", ") }));
  }

  function applyRoleGuidance() {
    const s = p.extForm && p.extForm.name ? `${p.extForm.name}\n${p.extForm.contact_path}\n${p.extForm.topics}\n${p.extForm.department}\n${p.extForm.title}\n${p.extForm.company}\n${p.extForm.email}\n${p.extForm.phone}\n${p.extForm.visibility_level}`.toLowerCase() : "";
    if (!s.trim()) return p.toast("Add role details first.", "err");
    p.setRoleGuidance(s.trim());
    const inferredDepartment = /manager|lead|director/.test(s) ? "Management" : /sales/.test(s) ? "Sales" : p.extForm.department;
    const inferredPath = /paperwork/.test(s) ? "dealer_paperwork" : /delivery/.test(s) ? "delivery_scheduling" : /service/.test(s) ? "service_scheduling" : /crm/.test(s) ? "crm_access" : /marketing|ads/.test(s) ? "marketing_materials" : p.extForm.contact_path;
    const inferredVisibility = /never contact|do not contact|manager only|escalation/i.test(s) ? "3" : /manager|restricted|through lead/i.test(s) ? "2" : p.extForm.visibility_level;
    const selectedTopics = new Set(p.parseTopicsText(p.extForm.topics));
    if (s.includes("construction")) selectedTopics.add("building_construction");
    if (s.includes("timeline")) selectedTopics.add("timelines");
    if (s.includes("special order")) selectedTopics.add("special_orders");
    if (s.includes("warranty")) selectedTopics.add("warranty_claims");
    if (s.includes("delivery")) selectedTopics.add("delivery_reschedule");
    if (s.includes("crm")) selectedTopics.add("crm_workflows");
    if (s.includes("facebook")) selectedTopics.add("facebook_ads");
    if (s.includes("google")) selectedTopics.add("google_ads");
    if (s.includes("paperwork") || s.includes("deal")) selectedTopics.add("dealer_paperwork");
    p.setExtForm((f: any) => ({ ...f, department: inferredDepartment, contact_path: inferredPath, visibility_level: inferredVisibility, topics: [...selectedTopics].join(", ") }));
    p.toast("Applied suggested routing fields from role guidance.");
  }

  async function saveExternal() {
    if (p.editingUserRow) {
      p.setUserFormBusy(true);
      try {
        const managerId = p.extForm.reports_to_external_id.trim() || null;
        const managerIsUser = managerId ? p.userManagerProfileIdSet.has(managerId) : false;
        await postContactsDirectoryOp(p.organizationId, {
          op: "user_row_upsert", id: p.editingUserRow.id, profile_id: p.editingUserRow.profile_id, location_id: p.extForm.location_id || null, scope: p.extForm.scope.trim() || "org",
          department: p.extForm.department.trim() || null, contact_path: p.extForm.contact_path.trim() || null, visibility_level: Number(p.extForm.visibility_level) || 1,
          max_downstream_visibility_level: p.extForm.max_downstream_visibility_level ? Number(p.extForm.max_downstream_visibility_level) : null,
          topics: p.extForm.topics.trim() ? p.extForm.topics.split(",").map((t: string) => t.trim()) : [], is_active: true, affiliation: p.extForm.affiliation, hierarchy_participation: p.extForm.hierarchy_participation,
          reports_to_profile_id: managerId && managerIsUser ? managerId : null, reports_to_external_id: managerId && !managerIsUser ? managerId : null, allow_vendor_hierarchy_override: p.extForm.allow_vendor_hierarchy_override,
        });
        p.toast("User directory row updated."); p.setEditingUserRow(null); p.setExtForm(p.createEmptyExternalForm()); await p.load();
      } catch (e: unknown) { p.toast(p.formatUnknownError(e), "err"); } finally { p.setUserFormBusy(false); }
      return;
    }
    if (!p.extForm.name.trim()) return p.toast("Name is required.", "err");
    if (!p.hasAtLeastTwoWords(p.extForm.name)) return p.toast("Full name must include at least first and last name.", "err");
    const trimmedEmail = p.extForm.email.trim();
    if (trimmedEmail && !p.isLikelyValidEmail(trimmedEmail)) return p.toast("Please enter a valid email address.", "err");
    try {
      const managerId = p.extForm.reports_to_external_id.trim() || null;
      const managerIsUser = managerId ? p.userManagerProfileIdSet.has(managerId) : false;
      const saved = await saveWithFallbackAcknowledge({
        op: "external_upsert", id: p.extForm.id || undefined, name: p.extForm.name.trim(), email: trimmedEmail || null, phone: p.extForm.phone.trim() || null, company: p.extForm.company.trim() || null, title: p.extForm.title.trim() || null,
        department: p.extForm.department.trim() || null, contact_path: p.extForm.contact_path.trim() || null, visibility_level: Number(p.extForm.visibility_level) || 1, max_downstream_visibility_level: p.extForm.max_downstream_visibility_level ? Number(p.extForm.max_downstream_visibility_level) : null,
        location_id: p.extForm.location_id || null, scope: p.extForm.scope.trim() || "org", topics: p.extForm.topics.trim() ? p.extForm.topics.split(",").map((t: string) => t.trim()) : [], is_active: true,
        affiliation: p.extForm.affiliation, hierarchy_participation: p.extForm.hierarchy_participation, reports_to_external_id: managerId && !managerIsUser ? managerId : null, reports_to_profile_id: managerId && managerIsUser ? managerId : null,
        allow_vendor_hierarchy_override: p.extForm.allow_vendor_hierarchy_override, allow_vendor_reporting_line: p.extForm.allow_vendor_reporting_line,
      }, "This is the selected general support fallback. Save anyway and leave the organization in degraded routing state until you choose a replacement?", p.extForm.id ? "Contact updated." : "Contact added.");
      if (!saved) return;
      p.setExtForm(p.createEmptyExternalForm()); await p.load();
    } catch (e: unknown) { p.toast(p.formatUnknownError(e), "err"); }
  }

  async function deactivateExternal(row: ExternalContactRow) {
    if (!window.confirm(`Deactivate external contact “${row.name}”?`)) return;
    try {
      const saved = await saveWithFallbackAcknowledge({ op: "external_set_active", id: row.id, is_active: false }, "This is the selected general support fallback. Deactivate it anyway and leave the organization in degraded routing state until you choose a replacement?", "Deactivated.");
      if (!saved) return;
      await p.load();
    } catch (e: unknown) { p.toast(p.formatUnknownError(e), "err"); }
  }

  return { saveWithFallbackAcknowledge, setTopicEnabled, applyRoleGuidance, saveExternal, deactivateExternal };
}
